import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { ClientApiProps, yupPhoneTestOptions } from '../../common';
import { Address, AddressPicker, AddressSchema, AlertProvider, Button, Label, PhoneInput, RadioList, TextInput, ValidationHint, useValidatedState } from 'acma-spa';
import {
  Address as ApiAddress,
  IOperatorContact,
  IOperatorRegistryContact,
  OperatorContact,
  OperatorContactDetails,
  OperatorRegistryContact,
  getApiClient,
} from '../../api';

type IModel = Omit<IOperatorContact, 'address1'> &
  Omit<IOperatorRegistryContact, 'advertisedAddress'> & {
    address1?: Address;
    advertisedAddress?: Address;
    isAssessor?: boolean;
  };

export const schema: yup.ObjectSchema<IModel> = yup.object({
  id: yup.string(),
  title: yup.string(),
  firstName: yup.string().required().label('First Name'),
  lastName: yup.string().required().label('Last Name'),
  otherName: yup.string(),
  emailAddress: yup.string().email().required().label('Email address'),
  phone1: yup.string().required().label('Contact phone').test(yupPhoneTestOptions),
  phone2: yup.string().label('Other phone').test(yupPhoneTestOptions),
  address1: AddressSchema.default(undefined).required('Address is required'),

  // Advertised fields are only shown if the user is an assessor
  isAssessor: yup.boolean(),

  // Assessor advertised fields
  advertised: yup.boolean().when('isAssessor', {
    is: true,
    then: (schema) => schema.required('Field is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  advertisedEmail: yup.string().email().optional().label('Email'),
  advertisedPhone: yup.string().optional().label('Phone'),
  advertisedAddress: yup.object().default(undefined),
});

export interface ContactPageProps extends ClientApiProps {
  isAssessor: boolean;
  contact?: OperatorContact;
  registryContact?: OperatorRegistryContact;
  onSpinner: (text: string) => any;
  onChange: (contact: OperatorContact, registryContact?: OperatorRegistryContact) => any;
}

export const ContactPage: React.FC<ContactPageProps> = (props) => {
  const [model, setModel, errors, validate] = useValidatedState<IModel>({}, schema);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const containerId = 'contact-details';

  useEffect(() => {
    if (props.registryContact) setModel({ isAssessor: props.isAssessor, ...(props.contact ?? {}), ...props.registryContact }, ValidationHint.NoValidation);
    else setModel({ isAssessor: props.isAssessor, ...(props.contact ?? {}) }, ValidationHint.NoValidation);
  }, [props.contact, props.registryContact]);

  const onSave = async () => {
    const isValid = await validate('#' + containerId);
    if (!isValid) {
      return;
    }

    const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);

    var contact = new OperatorContact({ ...model, address1: new ApiAddress(model.address1) });
    var registryContact = props.registryContact
      ? new OperatorRegistryContact({ ...model, advertisedAddress: new ApiAddress(model.advertisedAddress) })
      : undefined;

    props.onSpinner('Saving...');
    try {
      await client.operatorUpdateContactDetails(
        new OperatorContactDetails({
          contact: contact,
          registryContact: registryContact,
        }),
      );
      setMessage('Saved successfully');
      setIsError(false);

      props.onChange(contact, registryContact);
    } catch (e: any) {
      setMessage(e.message ? e.message : 'Unexpected error.');
      setIsError(true);
    } finally {
      props.onSpinner('');
    }
    window.scroll(0, 0);
  };

  const infoMessage = (
    <>
      <p>
        Please note the following requirements for amateur radio accredited assessors:
        <ul>
          <li>
            an Amateur Radio Operator’s Certificate of Proficiency (Advanced), ACMA recognition certificate (Advanced) or the letter of confirmation (Advanced) you received from the ACMA
          </li>
          <li>
            for applicants in all states and territories except NSW: a copy of your Working with Children or Vulnerable People Check Clearance (WWCVPCC) from the state or territory you will be conducting and assessing amateur examinations
          </li>
          <li>
            for NSW applicants: if you do not have a WWCVPCC, a copy of a Nationally Coordinated Criminal History Check you have completed in NSW issued no more than 30 days before your application.
          </li>
        </ul>
      </p>
      <p>
        If applicable, please provide your new WWCVPCC or National Coordinated Criminal History Check by email to <a href="mailto:arassessors@acma.gov.au">arassessors@acma.gov.au</a>. For every state and territory except NSW, please ensure that your WWCVPCC is linked to the ACMA.
      </p>
    </>
  );

  return (
    <div id={containerId}>
      {message.length > 0 ?? <AlertProvider dismiss={true} messages={[message]} color={isError ? 'danger' : 'info'} />}

      <div className="row">
        <div className="col-sm-4 mb-3">
          <Label id="title" label="Title" error={errors?.title}>
            <TextInput value={model.title || ''} onChange={(v) => setModel({ title: v })} maxLength={400} />
          </Label>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-4 mb-3">
          <Label id="firstName" label="First Name" error={errors?.firstName} required={true}>
            <TextInput value={model.firstName || ''} onChange={(v) => setModel({ firstName: v })} maxLength={149} />
          </Label>
        </div>
        <div className="col-sm-4 mb-3">
          <Label id="lastName" label="Last Name" error={errors?.lastName} required={true}>
            <TextInput value={model.lastName || ''} onChange={(v) => setModel({ lastName: v })} maxLength={149} />
          </Label>
        </div>
        <div className="col-sm-4 mb-3">
          <Label id="otherName" label="Other Name" error={errors?.otherName}>
            <TextInput value={model.otherName || ''} onChange={(v) => setModel({ otherName: v })} maxLength={149} />
          </Label>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-4 mb-3">
          <Label id="emailAddress" label="Email address" error={errors?.emailAddress} required={true}>
            <TextInput value={model.emailAddress || ''} onChange={(v) => setModel({ emailAddress: v })} maxLength={400} />
          </Label>
        </div>
        <div className="col-sm-4 mb-3">
          <Label id="phone1" label="Contact phone" error={errors?.phone1} required={true}>
            <PhoneInput value={model.phone1 || ''} id="phone1" onChange={(v) => setModel({ phone1: v })} maxLength={100} />
          </Label>
        </div>
        <div className="col-sm-4 mb-3">
          <Label id="phone2" label="Other phone" error={errors?.phone2}>
            <PhoneInput value={model.phone2 || ''} id="phone2" onChange={(v) => setModel({ phone2: v })} maxLength={100} />
          </Label>
        </div>
      </div>
      <div className="row">
        <div className="col mb-3">
          <Label id="address1" label="Contact address" error={errors?.address1} required={true}>
            <AddressPicker value={model.address1 || null} id="address1" onChange={(address) => setModel({ address1: address || undefined })} />
          </Label>
        </div>
      </div>

      {props.registryContact ? (
        <>
          <div className="row">
            <div className="col-sm-4 mb-3">
              <Label id="advertised" label="Advertise on the registry?" error={errors?.advertised} required>
                <RadioList<boolean | undefined>
                  id="advertised"
                  value={model.advertised}
                  options={[
                    {
                      label: 'Yes',
                      value: true,
                    },
                    {
                      label: 'No',
                      value: false,
                    },
                  ]}
                  onChange={(advertised) => {
                    setModel({ advertised });
                  }}
                />
              </Label>
            </div>
            <div className="col-sm-4 mb-3" style={{ display: model.advertised == true ? '' : 'none' }}>
              <Label id="advertisedEmail" label="Advertised emails" error={errors?.advertisedEmail}>
                <TextInput value={model.advertisedEmail || ''} onChange={(v) => setModel({ advertisedEmail: v })} maxLength={400} />
              </Label>
            </div>
            <div className="col-sm-4 mb-3" style={{ display: model.advertised == true ? '' : 'none' }}>
              <Label id="advertisedPhone" label="Advertised phone" error={errors?.advertisedPhone}>
                <TextInput value={model.advertisedPhone || ''} onChange={(v) => setModel({ advertisedPhone: v })} maxLength={100} />
              </Label>
            </div>
          </div>
          <div className="row" style={{ display: model.advertised == true ? '' : 'none' }}>
            <div className="col mb-3">
              <Label id="advertisedAddress" label="Advertised address" error={errors?.advertisedAddress}>
                <AddressPicker value={model.advertisedAddress || null} onChange={(address) => setModel({ advertisedAddress: address || undefined })} />
              </Label>
            </div>
          </div>

          <div className="alert alert-info">{infoMessage}</div>
        </>
      ) : (
        <></>
      )}

      <Button label="Save" id="save-contact-details" onClick={onSave} />
    </div>
  );
};
